/**
 * Performance testing utilities for the demo application
 */

import { PerformanceMonitor } from './assetOptimizer'
import { AnimationPerformanceMonitor } from './animationOptimizer'

export interface PerformanceTestResults {
  loadTime: number
  renderTime: number
  interactionTime: number
  fps: number
  coreWebVitals: {
    lcp?: number
    fid?: number
    cls?: number
    fcp?: number
    ttfb?: number
  }
  bundleSize: {
    total: number
    chunks: Record<string, number>
  }
  recommendations: string[]
}

export class PerformanceTest {
  private static results: Partial<PerformanceTestResults> = {}
  
  // Run comprehensive performance test
  static async runFullTest(): Promise<PerformanceTestResults> {
    console.log('🧪 Starting performance test...')
    
    // Test load time
    const loadTime = this.measureLoadTime()
    
    // Test render time
    const renderTime = this.measureRenderTime()
    
    // Test interaction time
    const interactionTime = await this.measureInteractionTime()
    
    // Get FPS
    const fps = AnimationPerformanceMonitor.getCurrentFPS()
    
    // Get Core Web Vitals
    const coreWebVitals = await PerformanceMonitor.getCoreWebVitals()
    
    // Estimate bundle size
    const bundleSize = await this.estimateBundleSize()
    
    // Generate recommendations
    const recommendations = this.generateRecommendations({
      loadTime,
      renderTime,
      interactionTime,
      fps,
      coreWebVitals,
      bundleSize
    })
    
    const results: PerformanceTestResults = {
      loadTime,
      renderTime,
      interactionTime,
      fps,
      coreWebVitals,
      bundleSize,
      recommendations
    }
    
    console.log('📊 Performance Test Results:', results)
    return results
  }
  
  // Measure load time
  private static measureLoadTime(): number {
    if (performance.timing) {
      return performance.timing.loadEventEnd - performance.timing.navigationStart
    }
    return 0
  }
  
  // Measure render time
  private static measureRenderTime(): number {
    if (performance.timing) {
      return performance.timing.domContentLoadedEventEnd - performance.timing.domLoading
    }
    return 0
  }
  
  // Measure interaction time
  private static async measureInteractionTime(): Promise<number> {
    return new Promise((resolve) => {
      const startTime = performance.now()
      
      // Simulate user interaction
      const button = document.querySelector('.professional-btn')
      if (button) {
        const clickEvent = new MouseEvent('click', { bubbles: true })
        button.dispatchEvent(clickEvent)
        
        requestAnimationFrame(() => {
          const endTime = performance.now()
          resolve(endTime - startTime)
        })
      } else {
        resolve(0)
      }
    })
  }
  
  // Estimate bundle size
  private static async estimateBundleSize(): Promise<{ total: number; chunks: Record<string, number> }> {
    try {
      // In a real implementation, this would analyze the actual bundle
      // For demo purposes, we'll estimate based on loaded resources
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[]
      
      let total = 0
      const chunks: Record<string, number> = {}
      
      resources.forEach(resource => {
        if (resource.name.includes('.js') || resource.name.includes('.css')) {
          const size = resource.transferSize || 0
          total += size
          
          const filename = resource.name.split('/').pop() || 'unknown'
          chunks[filename] = size
        }
      })
      
      return { total, chunks }
    } catch (error) {
      console.warn('Could not estimate bundle size:', error)
      return { total: 0, chunks: {} }
    }
  }
  
  // Generate performance recommendations
  private static generateRecommendations(metrics: Partial<PerformanceTestResults>): string[] {
    const recommendations: string[] = []
    
    // Load time recommendations
    if (metrics.loadTime && metrics.loadTime > 3000) {
      recommendations.push('Consider implementing more aggressive code splitting to reduce initial bundle size')
      recommendations.push('Enable compression (gzip/brotli) on your server')
      recommendations.push('Implement service worker caching for repeat visits')
    }
    
    // Render time recommendations
    if (metrics.renderTime && metrics.renderTime > 1000) {
      recommendations.push('Consider server-side rendering (SSR) for faster initial render')
      recommendations.push('Implement critical CSS inlining')
      recommendations.push('Reduce the number of render-blocking resources')
    }
    
    // FPS recommendations
    if (metrics.fps && metrics.fps < 50) {
      recommendations.push('Reduce animation complexity for better frame rate')
      recommendations.push('Use CSS transforms instead of changing layout properties')
      recommendations.push('Consider implementing animation throttling for low-end devices')
    }
    
    // Core Web Vitals recommendations
    if (metrics.coreWebVitals?.lcp && metrics.coreWebVitals.lcp > 2500) {
      recommendations.push('Optimize Largest Contentful Paint by preloading critical images')
      recommendations.push('Consider using a CDN for faster asset delivery')
    }
    
    if (metrics.coreWebVitals?.cls && metrics.coreWebVitals.cls > 0.1) {
      recommendations.push('Reduce Cumulative Layout Shift by setting explicit dimensions for images')
      recommendations.push('Avoid inserting content above existing content')
    }
    
    if (metrics.coreWebVitals?.fid && metrics.coreWebVitals.fid > 100) {
      recommendations.push('Improve First Input Delay by reducing JavaScript execution time')
      recommendations.push('Consider code splitting to reduce main thread blocking')
    }
    
    // Bundle size recommendations
    if (metrics.bundleSize?.total && metrics.bundleSize.total > 500000) { // 500KB
      recommendations.push('Bundle size is large - consider implementing dynamic imports')
      recommendations.push('Analyze bundle composition and remove unused dependencies')
      recommendations.push('Enable tree shaking to eliminate dead code')
    }
    
    // General recommendations
    if (recommendations.length === 0) {
      recommendations.push('Performance looks good! Consider monitoring these metrics over time')
      recommendations.push('Implement performance budgets to prevent regression')
    }
    
    return recommendations
  }
  
  // Quick performance check
  static quickCheck(): {
    status: 'good' | 'warning' | 'poor'
    message: string
    metrics: {
      fps: number
      loadTime: number
      supportsHardwareAcceleration: boolean
    }
  } {
    const fps = AnimationPerformanceMonitor.getCurrentFPS()
    const loadTime = this.measureLoadTime()
    const supportsHardwareAcceleration = AnimationPerformanceMonitor.supportsHardwareAcceleration()
    
    let status: 'good' | 'warning' | 'poor' = 'good'
    let message = 'Performance is optimal'
    
    if (fps < 45 || loadTime > 5000) {
      status = 'poor'
      message = 'Performance issues detected - consider optimizations'
    } else if (fps < 55 || loadTime > 3000) {
      status = 'warning'
      message = 'Performance could be improved'
    }
    
    return {
      status,
      message,
      metrics: {
        fps,
        loadTime,
        supportsHardwareAcceleration
      }
    }
  }
  
  // Monitor performance continuously
  static startContinuousMonitoring(interval = 5000): () => void {
    const intervalId = setInterval(() => {
      const check = this.quickCheck()
      
      if (check.status === 'poor') {
        console.warn('⚠️ Performance degradation detected:', check)
      }
    }, interval)
    
    return () => clearInterval(intervalId)
  }
  
  // Export performance data
  static exportResults(results: PerformanceTestResults): string {
    const timestamp = new Date().toISOString()
    const userAgent = navigator.userAgent
    
    const report = {
      timestamp,
      userAgent,
      results,
      environment: {
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        connection: (navigator as any).connection?.effectiveType || 'unknown',
        deviceMemory: (navigator as any).deviceMemory || 'unknown',
        hardwareConcurrency: navigator.hardwareConcurrency || 'unknown'
      }
    }
    
    return JSON.stringify(report, null, 2)
  }
}